/* ══════════════════════════════════════════════════════════════════════════
   Atribución — de qué anuncio viene cada persona
   ──────────────────────────────────────────────────────────────────────────
   Google Ads sabe cuántos clics compró; sólo el estudio sabe cuáles de esos
   clics terminaron en un caso firmado. Lo que falta es el hilo entre ambos, y
   ese hilo es el gclid: el identificador que Google añade a la URL final de
   cada clic en un anuncio.

   Aquí se captura al aterrizar, se guarda 90 días (la ventana de conversión
   de Google Ads) y se adjunta a cada lead que se crea. Con eso, más adelante,
   se pueden subir conversiones offline a Ads —"consulta agendada", "caso
   firmado" con su valor— y que la puja automática optimice hacia casos y no
   hacia clics baratos.

   Se guarda el último clic de anuncio, no el primero: es el criterio que usa
   Google Ads para atribuir, y mezclarlos daría cifras que no cuadran con la
   plataforma.
   ══════════════════════════════════════════════════════════════════════════ */

const CLAVE = 'sv_atribucion';
const VENTANA_DIAS = 90;

export interface Atribucion {
  /** Identificador del clic. gclid en búsqueda; wbraid/gbraid cuando el
      usuario no acepta cookies o viene de iOS. */
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  /** Variante de portada que vio (?t=despido, ?t=pension…). */
  variante?: string;
  /** Página de aterrizaje y momento del clic, en ISO. */
  landing?: string;
  ts?: string;
}

const CAMPOS_UTM = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
] as const;

function leerGuardada(): Atribucion | null {
  try {
    const crudo = localStorage.getItem(CLAVE);
    if (!crudo) return null;
    const a = JSON.parse(crudo) as Atribucion;
    if (!a.ts) return a;
    const edadDias = (Date.now() - new Date(a.ts).getTime()) / 86_400_000;
    if (edadDias > VENTANA_DIAS) {
      localStorage.removeItem(CLAVE);
      return null;
    }
    return a;
  } catch {
    // Modo privado o almacenamiento bloqueado: se sigue sin atribución.
    return null;
  }
}

/* Lee la URL al aterrizar y guarda la atribución si el visitante viene de un
   anuncio. Llamar una sola vez al iniciar, antes de que React monte, para no
   perder los parámetros si otra parte del código limpia la URL. */
export function capturarAtribucion(): void {
  if (typeof window === 'undefined') return;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }

  const gclid = params.get('gclid') || undefined;
  const wbraid = params.get('wbraid') || undefined;
  const gbraid = params.get('gbraid') || undefined;
  const utmSource = params.get('utm_source') || undefined;

  // Sin identificador de clic ni utm_source no hay nada que atribuir: es
  // tráfico orgánico o directo, y se respeta la atribución anterior si existe.
  if (!gclid && !wbraid && !gbraid && !utmSource) return;

  const a: Atribucion = {
    gclid, wbraid, gbraid,
    variante: params.get('t') || undefined,
    landing: window.location.pathname + window.location.search,
    ts: new Date().toISOString(),
  };
  for (const campo of CAMPOS_UTM) {
    const v = params.get(campo);
    if (v) a[campo] = v;
  }

  try {
    localStorage.setItem(CLAVE, JSON.stringify(a));
  } catch {
    /* sin almacenamiento, la atribución dura lo que dure la pestaña */
  }
}

/** Atribución vigente, o null si la persona no viene de un anuncio. */
export function getAtribucion(): Atribucion | null {
  return leerGuardada();
}

/* Código corto y legible del origen, para pegarlo en el mensaje de WhatsApp.
   WhatsApp es la acción principal del sitio y una conversación de WhatsApp no
   deja rastro en el panel: sin este código, la captadora no tiene forma de
   saber si quien escribe llegó por un anuncio ni por cuál. Se mantiene breve a
   propósito —lo va a leer el cliente antes de enviarlo— y se omite del todo
   cuando no hay nada que atribuir, para que el tráfico orgánico vea el mensaje
   limpio de siempre. */
export function codigoAtribucion(): string | null {
  const a = leerGuardada();
  if (!a) return null;
  return a.variante || a.utm_campaign || 'ads';
}
